import { createContext, useContext, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';

const SESSION_KEY = 'smd-tracing-navigation';
const STACK_KEY = 'smd-nav-key-stack';
const INDEX_KEY = 'smd-nav-stack-index';
const CLOSE_KEY = 'smd-via-close';

const IN_DEBUG_MODE = process.env.REACT_APP_ENVIRONMENT === 'local';

function logStep(...args) {
  if (IN_DEBUG_MODE) {
    console.log(SESSION_KEY, ...args);
  }
}

function findKeyWithLowestValue(obj) {
  let lowestKey = null;
  let lowestValue = Infinity;

  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'number' && value < lowestValue) {
      lowestValue = value;
      lowestKey = key;
    }
  }

  return lowestKey;
}

export const NavigationTracingContext = createContext();

function updateState(state) {
  sessionStorage?.setItem(SESSION_KEY, JSON.stringify(state));
}

function getLastState() {
  try {
    const currentBookmarks = sessionStorage?.getItem(SESSION_KEY) || '{}';
    return JSON.parse(currentBookmarks);
  } catch {
    return {};
  }
}

function getStack() {
  return JSON.parse(sessionStorage.getItem(STACK_KEY)) || [];
}

function setStack(stack) {
  sessionStorage.setItem(STACK_KEY, JSON.stringify(stack));
}

function getIndex() {
  return parseInt(sessionStorage.getItem(INDEX_KEY) || '0', 10);
}

function setIndex(index) {
  sessionStorage.setItem(INDEX_KEY, index.toString());
}

export function NavigationTracingProvider({ children }) {
  const location = useLocation();
  const history = useHistory();

  function incrementPoints() {
    const newPoints = Object.fromEntries(
      Object.entries(getLastState()).map(([key, value]) => [key, value + 1])
    );
    updateState(newPoints);
  }

  function decrementPoints() {
    const newPoints = Object.fromEntries(
      Object.entries(getLastState())
        .map(([key, value]) => [key, value - 1])
        .filter(([_, value]) => value > 0)
    );
    updateState(newPoints);
  }

  function createStartingPoint(key) {
    const lastState = { ...getLastState() };
    lastState[window.location.pathname] = 0;
    updateState(lastState);

    const stack = getStack();
    if (stack.length > 100) stack.shift();
    stack.push(key);
    setStack(stack);
    setIndex(stack.length - 1);

    logStep('Starting point created: ', lastState);
  }

  function navigateToLatest() {
    const points = { ...getLastState() };
    const latestPointKey = findKeyWithLowestValue(points);
    const stepsToWalk = points[latestPointKey];

    // Update the rest
    Object.keys(points).forEach((key) => {
      if (points[key] - stepsToWalk > 0) {
        points[key] = points[key] - stepsToWalk;
      } else {
        delete points[key];
      }
    });

    updateState(points);

    sessionStorage?.setItem(CLOSE_KEY, 'true');

    if (stepsToWalk > 0) {
      logStep(`returning ${stepsToWalk} steps`);
      history.go(-Math.abs(stepsToWalk));
    } else {
      logStep(`No steps to return, redirecting to /.`);
      history.push('/');
    }
  }

  useEffect(() => {
    if (history?.action === 'REPLACE') {
      return;
    }

    logStep(
      'location change: ',
      history?.action,
      location?.key,
      `history size: ${history?.length}`,
      getLastState()
    );

    const viaCloseButton = sessionStorage?.getItem(CLOSE_KEY) === 'true';

    if (history?.action === 'POP') {
      const stack = getStack();
      const newIndex = stack.indexOf(location?.key);
      const oldIndex = getIndex();

      if (newIndex < oldIndex) {
        if (!viaCloseButton) decrementPoints();
      } else if (newIndex > oldIndex) {
        if (!viaCloseButton) incrementPoints();
      }

      sessionStorage?.setItem(CLOSE_KEY, undefined);

      setIndex(newIndex);

      return;
    }

    incrementPoints();

    const stack = getStack();
    stack.push(location?.key);
    setStack(stack);
    setIndex(stack.length - 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const value = {
    createStartingPoint: () => createStartingPoint(location?.key),
    navigateToLatest,
  };

  return (
    <NavigationTracingContext.Provider value={value}>
      {children}
    </NavigationTracingContext.Provider>
  );
}

export function useNavigationTracing() {
  const context = useContext(NavigationTracingContext);
  if (!context) {
    throw new Error(
      'useNavigationTracing must be used within a NavigationTracingProvider'
    );
  }
  return context;
}
